import { PropertyImage } from '@/types'
import {
  Badge,
  Box,
  Flex,
  Grid,
  IconButton,
  Image,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { Star, Upload, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'

type ImageType = Omit<PropertyImage, 'id' | 'created_at' | 'property_id'> & {
  file?: File // Optional for new uploads
}

interface ImageUploadProps {
  images: ImageType[]
  onImagesChange: (images: ImageType[]) => void
  maxImages?: number
  maxSizePerImage?: number // in MB
}

export function ImageUpload({
  images,
  onImagesChange,
  maxImages = 10,
  maxSizePerImage = 5,
}: ImageUploadProps) {
  const toast = useToast()
  const [previews, setPreviews] = useState<(string | null)[]>(() => {
    // Initialize previews with existing images
    return images.map((image: ImageType) => {
      if (image.url) {
        return image.url
      }
      return image.file ? URL.createObjectURL(image.file) : null
    })
  })

  useEffect(() => {
    // Generate previews for existing images (PropertyImage)
    const existingPreviews = images.map((image) => {
      if (image.url) {
        return image.url
      }
      return image.file ? URL.createObjectURL(image.file) : null
    })
    setPreviews(existingPreviews)

    // Cleanup object URLs on unmount
    return () => {
      existingPreviews.forEach((preview) => {
        if (preview && preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview)
        }
      })
    }
  }, [images])

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      // Validate file sizes
      const oversizedFiles = acceptedFiles.filter(
        (file) => file.size > maxSizePerImage * 1024 * 1024
      )

      if (oversizedFiles.length > 0) {
        toast({
          title: 'Arquivos muito grandes',
          description: `Algumas imagens excedem ${maxSizePerImage}MB e foram ignoradas`,
          status: 'warning',
          duration: 5000,
        })
      }

      const validFiles = acceptedFiles.filter(
        (file) => file.size <= maxSizePerImage * 1024 * 1024
      )

      // Check total count
      const totalFiles = images.length + validFiles.length
      if (totalFiles > maxImages) {
        toast({
          title: 'Muitas imagens',
          description: `Máximo de ${maxImages} imagens permitidas`,
          status: 'warning',
          duration: 5000,
        })
        return
      }

      // Add new images
      const newImages = [
        ...images,
        ...validFiles.map(
          (file) =>
            ({
              file,
              stays_id: null,
              url: URL.createObjectURL(file),
              is_primary: images.length === 0, // Default to true for the first upload
            } as ImageType)
        ),
      ]
      onImagesChange(newImages)

      // Create previews for new files
      const newPreviews = validFiles.map((file) => URL.createObjectURL(file))
      setPreviews((prev) => [...prev, ...newPreviews])
    },
    [images, maxImages, maxSizePerImage, onImagesChange, toast]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
    },
    multiple: true,
  })

  const removeImage = (index: number) => {
    const imageToRemove = images[index]

    const newImages = images.filter((_, i) => i !== index)
    if (imageToRemove.is_primary && newImages.length > 0) {
      newImages[0].is_primary = true
    }
    onImagesChange(newImages)

    // const previewToRemove = previews[index]
    // if (previewToRemove && previewToRemove.startsWith('blob:')) {
    //   URL.revokeObjectURL(previewToRemove)
    // }

    // setPreviews((prev) => prev.filter((_, i) => i !== index))
  }

  const setMainImage = (index: number) => {
    const newImages = images.map((image, i) => ({
      ...image,
      is_primary: i === index,
    }))
    onImagesChange(newImages)
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Upload Area */}
      <Box
        {...getRootProps()}
        border="2px dashed"
        borderColor={isDragActive ? 'blue.400' : 'gray.300'}
        borderRadius="lg"
        p={8}
        textAlign="center"
        cursor="pointer"
        bg={isDragActive ? 'blue.50' : 'gray.50'}
        transition="all 0.2s"
        _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
      >
        <input {...getInputProps()} />
        <VStack spacing={2}>
          <Upload size={32} color={isDragActive ? '#3182CE' : '#A0ADB8'} />
          <Text
            fontWeight="medium"
            color={isDragActive ? 'blue.600' : 'gray.600'}
          >
            {isDragActive
              ? 'Solte as imagens aqui...'
              : 'Arraste imagens ou clique para selecionar'}
          </Text>
          <Text fontSize="sm" color="gray.500">
            Máximo {maxImages} imagens, até {maxSizePerImage}MB cada
          </Text>
        </VStack>
      </Box>

      {/* Image Previews */}
      {images.length > 0 && (
        <Box>
          <Text fontWeight="medium" mb={3}>
            Imagens Selecionadas ({images.length}/{maxImages})
          </Text>
          <Grid templateColumns="repeat(auto-fill, minmax(120px, 1fr))" gap={4}>
            {images.map((image, index) => (
              <Box key={index} position="relative">
                <Image
                  src={previews[index] ?? ''}
                  alt={`Preview ${index + 1}`}
                  borderRadius="md"
                  objectFit="cover"
                  w="120px"
                  h="120px"
                  border={image.is_primary ? '3px solid' : '1px solid'}
                  borderColor={image.is_primary ? 'blue.500' : 'gray.200'}
                />

                {/* Main image badge */}
                {image.is_primary && (
                  <Badge
                    position="absolute"
                    top={1}
                    left={1}
                    colorScheme="blue"
                    fontSize="xs"
                  >
                    Principal
                  </Badge>
                )}

                {/* Action buttons */}
                <Flex position="absolute" top={1} right={1} gap={1}>
                  {!image.is_primary && (
                    <IconButton
                      aria-label="Definir como principal"
                      icon={<Star size={14} />}
                      size="xs"
                      colorScheme="yellow"
                      onClick={() => setMainImage(index)}
                    />
                  )}
                  <IconButton
                    aria-label="Remover imagem"
                    icon={<X size={14} />}
                    size="xs"
                    colorScheme="red"
                    onClick={() => removeImage(index)}
                  />
                </Flex>

                {/* File name */}
                <Text
                  fontSize="xs"
                  color="gray.600"
                  mt={1}
                  noOfLines={1}
                  textAlign="center"
                >
                  {image?.file && image?.file.name
                    ? image?.file.name
                    : `Imagem ${index + 1}`}
                </Text>
              </Box>
            ))}
          </Grid>
        </Box>
      )}
    </VStack>
  )
}
